// صفحة الإبلاغ عن مخالفة — نموذج واحد، إرسال إلى API
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { getMosqueById } from "../../../services/mosqueService";
import { isAuthenticated, getIdentityFromToken } from "../../../services/authService";
import { createMosqueIncident } from "../../../services/mosqueIncidentService";
import type { CreateMosqueIncidentParams } from "../../../services/mosqueIncidentService";

type MosqueInfo = { name: string; number: string; regionName: string; cityName: string };

type AuthUserIdentity = {
  name: string;
  email: string;
  phone: string;
  nationalID: string;
  dob: string;
  nationality: string;
  dateOfBirthH?: string;
};

function safeJsonParse<T = any>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * ✅ Reads logged-in user identity only.
 * ✅ Does NOT read donor data, because incident report does not require donor.
 *
 * Expected verify OTP response:
 * {
 *   token: "...",
 *   user: { name, email, phone, nationalID, dob, nationality },
 *   donor: {...}
 * }
 */
function getLoggedInUserIdentity(): AuthUserIdentity {
  const tokenIdentity = (getIdentityFromToken?.() ?? {}) as any;

  // Try common storage keys where login usually saves verify OTP response or user object.
  // If your authService uses another key, add it here or expose a getAuthUser() function from authService.
  const storageKeys = [
    "authUser",
    "user",
    "currentUser",
    "loginUser",
    "verifyOtpUser",
    "verifyOtpResponse",
    "authData",
    "auth",
    "residentPartnerProfile",
  ];

  let storedUser: any = null;

  for (const key of storageKeys) {
    const localValue = safeJsonParse<any>(localStorage.getItem(key));
    const sessionValue = safeJsonParse<any>(sessionStorage.getItem(key));
    const value = localValue ?? sessionValue;

    if (!value) continue;

    // Some apps save response.user, others save the whole verifyOtp response.
    const candidate = value.user ?? value;

    if (candidate?.nationalID || candidate?.nationalId || candidate?.PersonId || candidate?.iqamaNumber) {
      storedUser = candidate;
      break;
    }
  }

  const src = {
    ...(tokenIdentity ?? {}),
    ...(storedUser ?? {}),
  };

  return {
    name:
      src.name ??
      src.fullName ??
      src.NameAR ??
      [src.firstName, src.secondName, src.thirdName, src.lastName].filter(Boolean).join(" ") ??
      "",
    email: src.email ?? "",
    phone: src.phone ?? src.mobile ?? src.mobileNumber ?? "",
    nationalID: src.nationalID ?? src.nationalId ?? src.PersonId ?? src.iqamaNumber ?? "",
    dob: src.dob ?? src.dateOfBirth ?? src.dateOfBirthG ?? "",
    nationality: src.nationality ?? src.countryName ?? "المملكة العربية السعودية",
    dateOfBirthH: src.dateOfBirthH ?? "",
  };
}


/** قيم نوع البلاغ المرسلة للـ API (params.type) */
const INCIDENT_TYPES = [
  { value: "", label: "اختر نوع البلاغ" },
  { value: "encroachment", label: "بلاغ عن وجود تعدي" },
  { value: "imam_muezzin_related", label: "بلاغ يتعلق بالإمام والمؤذن" },
  { value: "maintenance_cleanliness", label: "بلاغ يتعلق بأعمال الصيانة" },
];

/** تفاصيل النوع: value للعرض، indecentType = ID من جدول mosque_incidence_report_type يُرسل كـ params.indecent_type */
const INCIDENT_SUBTYPES: Record<string, { value: string; label: string; indecentType: number }[]> = {
  encroachment: [
    { value: "", label: "اختر التفاصيل", indecentType: 0 },
    { value: "land", label: "تعدي على جزء من أرض المسجد", indecentType: 1 },
    { value: "facilities", label: "تعدي على مرافق المسجد", indecentType: 3 },
    { value: "electricity", label: "تعدي على خدمات الكهرباء", indecentType: 4 },
    { value: "water", label: "تعدي على خدمات المياه", indecentType: 5 },
    { value: "other", label: "أخرى", indecentType: 6 },
  ],
  imam_muezzin_related: [
    { value: "", label: "اختر التفاصيل", indecentType: 0 },
    { value: "imam_absence", label: "كثرة غياب الإمام", indecentType: 7 },
    { value: "muezzin_absence", label: "كثرة غياب المؤذن", indecentType: 8 },
    { value: "other", label: "أخرى", indecentType: 9 },
  ],
  maintenance_cleanliness: [
    { value: "", label: "اختر التفاصيل", indecentType: 0 },
    { value: "cleanliness", label: "عدم نظافة المسجد", indecentType: 10 },
    { value: "toilets_clean", label: "عدم نظافة دورات المياه", indecentType: 11 },
    { value: "lighting", label: "حاجة المسجد إلى صيانة للإنارة", indecentType: 12 },
    { value: "ac", label: "حاجة المسجد لصيانة التكييف", indecentType: 13 },
    { value: "plumbing", label: "حاجة المسجد لصيانة السباكة في دورات المياه", indecentType: 14 },
  ],
};

/** صيغة التاريخ والوقت المتوقعة من الـ API: %Y-%m-%d %H:%M:%S */
function formatReportDateTime(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64 ?? "");
    };
    reader.onerror = () => reject(new Error("فشل قراءة الملف"));
    reader.readAsDataURL(file);
  });
}

export default function MosqueIncidentReportingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const mosqueId = id ? Number(id) : NaN;
  const validId = Number.isFinite(mosqueId) && mosqueId > 0;

  const [mosqueInfo, setMosqueInfo] = useState<MosqueInfo | null>(null);
  const [loadingMosque, setLoadingMosque] = useState(validId);

  const [type, setType] = useState("");
  const [subtype, setSubtype] = useState("");
  const [description, setDescription] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [countryName, setCountryName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dobHijri, setDobHijri] = useState("");
  const [loadingReporter, setLoadingReporter] = useState(true);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // توجيه غير المسجّل إلى تسجيل الدخول مع returnUrl للعودة بعد الدخول
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/benefaction/login?returnUrl=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [navigate, location.pathname]);

  const handleRemoveAttachment = useCallback(() => {
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  useEffect(() => {
    if (!validId) {
      setLoadingMosque(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getMosqueById(mosqueId);
        if (!cancelled && data) {
          setMosqueInfo({
            name: data.name ?? "",
            number: data.number ?? "",
            regionName: data.regionName ?? "",
            cityName: data.cityName ?? "",
          });
        } else if (!cancelled) setMosqueInfo(null);
      } catch {
        if (!cancelled) setMosqueInfo(null);
      } finally {
        if (!cancelled) setLoadingMosque(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mosqueId, validId]);

  // ✅ بيانات المبلّغ يجب أن تأتي من المستخدم المسجّل فقط
  // ❌ لا نستخدم donorService هنا لأن بلاغ المسجد لا يتطلب أن يكون المستخدم متبرعاً
  useEffect(() => {
    setLoadingReporter(true);

    if (!isAuthenticated()) {
      setLoadingReporter(false);
      return;
    }

    const identity = getLoggedInUserIdentity();

    setReporterName(identity.name);
    setNationalId(identity.nationalID);
    setEmail(identity.email);
    setMobileNumber(identity.phone);
    setCountryName(identity.nationality);
    setDateOfBirth(identity.dob);
    setDobHijri(identity.dateOfBirthH ?? "");

    setLoadingReporter(false);
  }, []);

  const openImagePreview = useCallback(() => {
    if (!attachedFile) return;
    const url = URL.createObjectURL(attachedFile);
    setImagePreviewUrl(url);
    setImagePreviewOpen(true);
  }, [attachedFile]);

  const closeImagePreview = useCallback(() => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl(null);
    setImagePreviewOpen(false);
  }, [imagePreviewUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDescriptionError(false);

    if (!type?.trim()) {
      setError("نوع البلاغ مطلوب.");
      return;
    }

    const subtypeOptions = INCIDENT_SUBTYPES[type];
    const selectedSubtype = subtypeOptions?.find((s) => s.value === subtype);
    const indecentTypeId = selectedSubtype?.indecentType ?? 0;

    if (!subtype?.trim() || indecentTypeId <= 0) {
      setError("تفاصيل البلاغ مطلوبة.");
      return;
    }

    if (!description?.trim()) {
      setError("يرجى كتابة وصف البلاغ.");
      setDescriptionError(true);
      return;
    }

    setDescriptionError(false);

    if (!validId || mosqueId <= 0) {
      setError("معرف المسجد غير صالح.");
      return;
    }

    // ✅ البلاغ الآن API خاص ويتطلب تسجيل دخول فقط، وليس بيانات متبرع
    if (!isAuthenticated()) {
      navigate(`/benefaction/login?returnUrl=${encodeURIComponent(location.pathname)}`, { replace: true });
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const nin = (nationalId ?? "").replace(/\D/g, "");
      const isCitizen = nin.startsWith("1");

      // ✅ Yaqeen requires DOB.
      // Saudi citizen normally uses Hijri DOB if available; resident uses Gregorian DOB.
      // If only verify OTP user.dob is available, send it as-is.
      const dobToSend =
        isCitizen
          ? dobHijri || dateOfBirth || undefined
          : dateOfBirth || dobHijri || undefined;

      if (!nationalId?.trim()) {
        setError("رقم الهوية غير متوفر من بيانات تسجيل الدخول.");
        setSubmitting(false);
        return;
      }

      if (!dobToSend) {
        setError("تاريخ الميلاد غير متوفر من بيانات تسجيل الدخول.");
        setSubmitting(false);
        return;
      }

      // Send the selected main category and its matching subtype ID.
      const params: CreateMosqueIncidentParams = {
        type,
        indecent_type: indecentTypeId,
        mosque_id: mosqueId,
        national_id: nationalId?.trim() || undefined,
        email: email || undefined,
        mobile_number: mobileNumber || undefined,
        country_name: countryName?.trim() || "غير محدد",
        date_of_birth: dobToSend,
        description: description || undefined,
        platform: "e_massajid",
        counter: 0,
        report_date_time: formatReportDateTime(new Date()),
      };

      if (attachedFile) {
        params.attachment = await fileToBase64(attachedFile);
        params.attachment_filename = attachedFile.name;
      }

      await createMosqueIncident(params);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "حدث خطأ أثناء إرسال البلاغ.");
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumb = [
    { label: "الرئيسية", to: "/" },
    { label: "المساجد", to: "/mosquesearch" },
    ...(validId ? [{ label: "تفاصيل المسجد", to: `/mosques/${id}` }] : []),
    { label: "الإبلاغ عن مخالفة", to: null },
  ];

  if (success) {
    return (
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8" dir="rtl">
        <div className="rounded-2xl border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-6 text-center">
          <p className="text-green-800 dark:text-green-200 text-lg font-medium mb-4">
            تم إرسال البلاغ إلى النظام بنجاح.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/mosques/${id}`)}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-sa-600 px-5 text-white font-[600] hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              العودة لتفاصيل المسجد
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-neutral-300 bg-white px-5 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-sa-500"
            >
              رجوع
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" dir="rtl">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
          {breadcrumb.map((b, i) => {
            const item = b as { label: string; to?: string };
            return (
              <li key={i} className="flex items-center">
                {item.to ? (
                  <Link to={item.to} className="hover:text-sa-700 hover:underline">{item.label}</Link>
                ) : (
                  <span className="font-medium text-neutral-800 dark:text-neutral-100">{item.label}</span>
                )}
                {item.to && <span className="mx-2 text-neutral-400">/</span>}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          الإبلاغ عن مخالفة
        </h1>
        <button
          type="button"
          onClick={() => (validId ? navigate(`/mosques/${id}`) : navigate(-1))}
          className="inline-flex h-11 items-center justify-center rounded-xl border border-neutral-300 bg-white px-4 text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-sa-500"
        >
          رجوع
        </button>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <header className="border-b border-neutral-200 px-5 py-4 dark:border-neutral-800">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 text-center">
            الإبلاغ عن مسجد
          </h2>
        </header>

        {validId && (
          <div className="border-b border-neutral-200 px-5 py-4 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/30">
            {loadingMosque ? (
              <p className="text-center text-sm text-neutral-500 dark:text-neutral-400">جاري تحميل بيانات المسجد…</p>
            ) : mosqueInfo ? (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <dt className="text-neutral-500 dark:text-neutral-400">اسم المسجد</dt>
                <dd className="text-neutral-900 dark:text-neutral-100 font-medium">{mosqueInfo.name || "—"}</dd>
                <dt className="text-neutral-500 dark:text-neutral-400">الرقم التسلسلي</dt>
                <dd className="text-neutral-900 dark:text-neutral-100">{mosqueInfo.number || "—"}</dd>
                <dt className="text-neutral-500 dark:text-neutral-400">المنطقة</dt>
                <dd className="text-neutral-900 dark:text-neutral-100">{mosqueInfo.regionName || "—"}</dd>
                <dt className="text-neutral-500 dark:text-neutral-400">المدينة</dt>
                <dd className="text-neutral-900 dark:text-neutral-100">{mosqueInfo.cityName || "—"}</dd>
              </dl>
            ) : null}
          </div>
        )}

        <div className="border-b border-neutral-200 px-5 py-4 dark:border-neutral-800 bg-neutral-50/30 dark:bg-neutral-800/20">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">بيانات المبلغ</h3>
          {loadingReporter ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">جاري تحميل بيانات المبلغ…</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-neutral-500 dark:text-neutral-400">اسم المبلغ</dt>
              <dd className="text-neutral-900 dark:text-neutral-100 font-medium">{reporterName || "—"}</dd>
              <dt className="text-neutral-500 dark:text-neutral-400">رقم الهوية</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{nationalId || "—"}</dd>
              <dt className="text-neutral-500 dark:text-neutral-400">رقم الجوال</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{mobileNumber || "—"}</dd>
              <dt className="text-neutral-500 dark:text-neutral-400">الجنسية</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{countryName || "—"}</dd>
              <dt className="text-neutral-500 dark:text-neutral-400">تاريخ الميلاد</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{dobHijri || dateOfBirth || "—"}</dd>
              <dt className="text-neutral-500 dark:text-neutral-400">تاريخ البلاغ</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{formatReportDateTime(new Date())}</dd>
            </dl>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 px-4 py-3 text-red-700 dark:text-red-300 text-sm space-y-2">
              <p>{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              نوع البلاغ <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={type}
              onChange={(e) => { setType(e.target.value); setSubtype(""); }}
              className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-2 focus:ring-sa-500"
            >
              {INCIDENT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {type && INCIDENT_SUBTYPES[type] && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                تفاصيل نوع البلاغ
              </label>
              <select
                value={subtype}
                onChange={(e) => setSubtype(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-2 focus:ring-sa-500"
              >
                {INCIDENT_SUBTYPES[type].map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              وصف البلاغ
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setError(null);
                setDescriptionError(false);
              }}
              placeholder="اشرح تفاصيل المخالفة أو الملاحظة..."
              rows={3}
              className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 placeholder:text-neutral-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 focus:border-sa-500 focus:ring-2 focus:ring-sa-500 min-h-[100px]"
            />
            {descriptionError && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-1">يرجى كتابة وصف البلاغ.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              إرفاق صورة (اختياري)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setAttachedFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-neutral-600 dark:text-neutral-400 file:mr-3 file:rounded-lg file:border-0 file:bg-sa-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-sa-700 hover:file:bg-sa-100 dark:file:bg-sa-900/30 dark:file:text-sa-300"
            />
            {attachedFile ? (
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 flex items-center gap-2 flex-wrap">
                <span>{attachedFile.name}</span>
                <button type="button" onClick={openImagePreview} className="text-sa-600 hover:text-sa-700 text-xs underline focus:outline-none">عرض</button>
                <button type="button" onClick={handleRemoveAttachment} className="text-red-600 hover:text-red-700 text-xs underline focus:outline-none">إزالة</button>
              </p>
            ) : (
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">لم يتم اختيار اي ملف</p>
            )}
          </div>

          <div className="rounded-xl border border-neutral-200 bg-neutral-50/50 dark:border-neutral-700 dark:bg-neutral-800/50 p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={declarationAccepted}
                onChange={(e) => setDeclarationAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-neutral-300 text-sa-600 focus:ring-sa-500"
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">
                أقر بأن جميع البيانات صحيحة وأتحمل كامل مسؤوليتها
              </span>
            </label>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={submitting || !declarationAccepted}
              className="inline-flex h-12 items-center justify-center rounded-xl bg-sa-600 px-6 text-white font-semibold hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "جاري الإرسال…" : "إرسال البلاغ"}
            </button>
          </div>
        </form>
      </div>

      {imagePreviewOpen && imagePreviewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={closeImagePreview}
          role="dialog"
          aria-modal="true"
          aria-label="معاينة الصورة"
        >
          <div className="relative max-h-[90vh] max-w-[90vw] rounded-xl bg-neutral-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={closeImagePreview} className="absolute -top-10 right-0 text-white hover:text-neutral-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white rounded px-2 py-1">إغلاق</button>
            <img src={imagePreviewUrl} alt="معاينة الصورة المرفقة" className="max-h-[85vh] max-w-full rounded-xl object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}